/* eslint-disable max-lines */
/* eslint-disable max-depth */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { AgGridModule } from '@ag-grid-community/angular';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  Column,
  ExcelExportParams,
  ExcelStyle,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { isEqual, merge } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, of, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import {
  AgBudgetAttributeComponentParams,
  AgBudgetGroupHeaderComponent,
} from '@features/ag-budget-group-header/ag-budget-group-header.component';
import { BudgetDataType } from '@features/budget/services/budget-grid.service';
import { BudgetService } from '@features/budget/services/budget.service';
import {
  attributeColumnDef,
  getAttributeColumns,
} from '@features/budget-attributes/services/budget-attributes.service';

import { AgPulseMinusComponent } from '@shared/ag-components/ag-pulse-minus/ag-pulse-minus.component';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { BudgetData, BudgetType, Currency, GqlService } from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import {
  OrganizationModel,
  OrganizationStore,
} from '@shared/store/organization/organization.store';
import { AgSetColumnsVisible, AuxExcelFormats } from '@shared/utils';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

export interface CompareBudgetVersion {
  budget_name: string;
  budget_type: BudgetType;
  budget_version_id: string;
}

export interface CompareGridData extends BudgetData {
  changed: boolean;
  from_activity_no: string;
  from_row?: BudgetData;
  from_total_cost: number;
  from_unit: number;
  from_unit_cost: number;
  missing_activity: boolean;
  to_activity_no: string;
  to_display_label: string;
  to_row: BudgetData;
  to_total_cost: number;
  to_unit: number;
  to_unit_cost: number;
  to_uom: string;
  variance_total_cost: number;
  variance_total_percent: number;
  variance_unit: number;
  variance_unit_cost: number;
}

// localstorage key for compare attributes
const CompareAttributesLSKey = 'compare_activities_header';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, AgGridModule, StickyGridDirective],
  selector: 'aux-compare-budget',
  styleUrls: ['./compare-budget.component.scss'],
  templateUrl: './compare-budget.component.html',
})
export class CompareBudgetComponent {
  public organizationQuery = inject(OrganizationQuery);

  private authQuery = inject(AuthQuery);
  private budgetService = inject(BudgetService);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private organizationStore = inject(OrganizationStore);
  private router = inject(Router);

  autoGroupColumnDef: ColDef = {
    cellRendererParams: {
      suppressCount: true,
    },
    field: 'activity_name',
    headerComponent: AgBudgetGroupHeaderComponent,
    headerComponentParams: {
      afterAttrToggle: ({ api }) => {
        api.sizeColumnsToFit();
      },
      expandLevel: () => -1,
      localStorageKey: CompareAttributesLSKey,
      template: `Activities`,
    } as AgBudgetAttributeComponentParams,
    headerName: 'Activities ',
    minWidth: 250,
    pinned: 'left',
    resizable: true,
    tooltipField: 'activity_name',
    width: 250,
  };
  @Output() budgetData = new EventEmitter<CompareGridData[]>();
  columnDefs: (ColDef | ColGroupDef)[] = [];
  fromBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);
  gridData$ = new BehaviorSubject<CompareGridData[]>([]);
  defaultColumns: (ColDef | ColGroupDef)[] = [];
  groupDefaultExpanded = 1;
  gridOptions = {
    columnDefs: this.defaultColumns,
    defaultColDef: {
      resizable: false,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    excelStyles: [
      ...this.createExcelStyles(),
      {
        dataType: 'Number',
        id: 'budgetCostNoSymbol',
        numberFormat: { format: AuxExcelFormats.Units },
      },
      {
        dataType: 'Number',
        id: 'budget-cost',
        numberFormat: { format: AuxExcelFormats.Cost },
      },
      {
        dataType: 'Number',
        id: 'budget-percent',
        numberFormat: { format: AuxExcelFormats.Percent },
      },
      {
        alignment: { horizontal: 'Left' },
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'first_row',
        interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
      },
      {
        alignment: { horizontal: 'Left' },
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'trial-vend-name',
        interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
      },
      {
        dataType: 'Number',
        id: 'budget-unit',
        numberFormat: { format: '#;(#);—' },
      },
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'header',
        interior: { color: '#094673', pattern: 'Solid' },
      },
      {
        font: {
          bold: true,
          color: '#FFFFFF',
          fontName: 'Arial',
          size: 11,
        },
        id: 'headerGroup',
        interior: { color: '#999999', pattern: 'Solid' },
      },
      {
        font: { fontName: 'Arial', size: 11 },
        id: 'cell',
      },
      {
        font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
        id: 'total_row_header',
        interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
      },
      {
        dataType: 'Number',
        font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
        id: 'total_row_percent',
        interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
        numberFormat: { format: AuxExcelFormats.Percent },
      },
    ],
    groupDefaultExpanded: this.groupDefaultExpanded,
    groupIncludeTotalFooter: false,
    suppressAggFuncInHeader: true,
    suppressCellFocus: true,
    suppressColumnVirtualisation: true,
  } as GridOptions;
  gridOptions$ = new BehaviorSubject<GridOptions>(this.gridOptions);
  loading$ = new BehaviorSubject(false);
  @Input() refetch?: Subject<boolean>;
  toBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);

  budget$ = combineLatest([
    this.fromBudgetVersion$,
    this.toBudgetVersion$,
    this.organizationQuery.selectActive(),
    this.refetch ? this.refetch : of(false),
  ]).pipe(
    distinctUntilChanged(isEqual),
    switchMap(([fromBudgetVersion, toBudgetVersion, organization]) => {
      if (organization?.currency) {
        this.org_currency = organization.currency;
      }

      if (!fromBudgetVersion) {
        return EMPTY;
      }

      if (!toBudgetVersion) {
        return EMPTY;
      }

      if (!organization) {
        return EMPTY;
      }

      this.setColumnDefs();

      this.loading$.next(true);

      return combineLatest([
        this.gqlService
          .listBudgetGrid$({
            budget_type: fromBudgetVersion.budget_type,
            budget_version_id: fromBudgetVersion.budget_version_id,
            in_month: false,
            vendor_id: organization.id,
          })
          .pipe(this.budgetService.budgetCacheMechanism()),
        this.gqlService
          .listBudgetGrid$({
            budget_type: toBudgetVersion.budget_type,
            budget_version_id: toBudgetVersion.budget_version_id,
            in_month: false,
            vendor_id: organization.id,
          })
          .pipe(this.budgetService.budgetCacheMechanism()),
      ]).pipe(
        tap(
          ([
            { data: from_data, success: from_success },
            { data: to_data, success: to_success },
          ]) => {
            const budget_data: CompareGridData[] = [];
            const added_activities: Set<string> = new Set<string>();

            if (from_success && to_success && from_data?.budget_data && to_data?.budget_data) {
              this.doesBudgetDataHaveMissingActivities = false;
              for (let i = 0; i < from_data.budget_data.length; i++) {
                const from_current_row = from_data.budget_data[i];
                let current_activity_id = '';

                if (
                  from_current_row?.activity_name &&
                  from_current_row?.activity_id &&
                  from_current_row?.group_index !== null // group index can be 0
                ) {
                  current_activity_id = from_current_row?.activity_id;
                  added_activities.add(`${current_activity_id}`);
                }

                const to_current_row =
                  to_data.budget_data.filter(
                    (toBudgetData: BudgetDataType) =>
                      toBudgetData.activity_id === current_activity_id
                  )[0] || null;

                const variance_unit =
                  (to_current_row?.unit_num || 0) - (from_current_row?.unit_num || 0) || 0;
                const variance_unit_cost =
                  (to_current_row?.contract_unit_cost || 0) -
                    (from_current_row?.contract_unit_cost || 0) || 0;
                const variance_total_cost =
                  (to_current_row?.contract_direct_cost || 0) -
                    (from_current_row?.contract_direct_cost || 0) || 0;
                const variance_total_percent = !from_current_row?.contract_direct_cost
                  ? 0
                  : (variance_total_cost || 0) / (from_current_row?.contract_direct_cost || 1);

                if (from_current_row && !to_current_row) {
                  this.doesBudgetDataHaveMissingActivities = true;
                }

                const getField = <T extends keyof BudgetDataType>(field: T): BudgetDataType[T] => {
                  return to_current_row ? to_current_row[field] : from_current_row[field];
                };

                budget_data.push({
                  ...from_current_row,
                  activity_name: getField('activity_name'),
                  activity_name_label: getField('activity_name_label'),
                  attributes: getField('attributes'),
                  changed: !!variance_unit || !!variance_unit_cost || !!variance_total_cost,
                  from_activity_no: from_current_row?.activity_no || '',
                  from_row: from_current_row,
                  from_total_cost: from_current_row?.contract_direct_cost || 0,
                  from_unit: from_current_row?.unit_num || 0,
                  from_unit_cost: from_current_row?.contract_unit_cost || 0,
                  group0: getField('group0'),
                  group1: getField('group1'),
                  group2: getField('group2'),
                  group3: getField('group3'),
                  group4: getField('group4'),
                  missing_activity: from_current_row && !to_current_row,
                  to_activity_no: to_current_row?.activity_no || '',
                  to_display_label: to_current_row?.display_label || '',
                  to_row: to_current_row,
                  to_total_cost: to_current_row?.contract_direct_cost || 0,
                  to_unit: to_current_row?.unit_num || 0,
                  to_unit_cost: to_current_row?.contract_unit_cost || 0,
                  to_uom: to_current_row?.uom || '',
                  variance_total_cost,
                  variance_total_percent,
                  variance_unit,
                  variance_unit_cost,
                });
              }

              for (let i = 0; i < to_data.budget_data.length; i++) {
                const to_row = to_data.budget_data[i];

                if (to_row) {
                  if (
                    to_row.activity_name &&
                    to_row.activity_id &&
                    to_row.group_index !== null &&
                    !added_activities.has(`${to_row.activity_id}`)
                  ) {
                    budget_data.push({
                      ...to_row,
                      changed:
                        !!to_row.unit_num ||
                        !!to_row.contract_unit_cost ||
                        !!to_row.contract_direct_cost,
                      from_activity_no: '',
                      from_total_cost: 0,
                      from_unit: 0,
                      from_unit_cost: 0,
                      missing_activity: false,
                      to_activity_no: to_row?.activity_no || '',
                      to_display_label: to_row?.display_label || '',
                      to_row,
                      to_total_cost: to_row?.contract_direct_cost || 0,
                      to_unit: to_row?.unit_num || 0,
                      to_unit_cost: to_row?.contract_unit_cost || 0,
                      to_uom: to_row?.uom || '',
                      variance_total_cost: to_row.contract_direct_cost || 0,
                      variance_total_percent: 0,
                      variance_unit: to_row.unit_num || 0,
                      variance_unit_cost: to_row.contract_unit_cost || 0,
                    });
                  }
                } else {
                  this.doesBudgetDataHaveMissingActivities = true;
                  budget_data.push({
                    changed: true,
                    from_total_cost: 0,
                    from_unit: 0,
                    from_unit_cost: 0,
                    missing_activity: true,
                    to_total_cost: 0,
                    to_unit: 0,
                    to_unit_cost: 0,
                    variance_total_cost: 0,
                    variance_total_percent: 0,
                    variance_unit: 0,
                    variance_unit_cost: 0,
                  } as CompareGridData);
                }
              }
            }

            const budgetData = budget_data
              .map((row: CompareGridData) =>
                row.cost_category === 'Discount'
                  ? {
                      ...row,
                      from_unit: 0,
                      from_unit_cost: 0,
                      to_unit: 0,
                      to_unit_cost: 0,
                      variance_unit: 0,
                      variance_unit_cost: 0,
                    }
                  : row
              )
              .map((row) => {
                const extraAttributes = row.attributes?.reduce(
                  (acc, a) => {
                    if (a.attribute_name && a.attribute_value) {
                      acc[getEncodedAttributeName(a.attribute_name)] = a.attribute_value;
                    }

                    return acc;
                  },
                  {} as Record<string, string>
                );

                return { ...row, ...extraAttributes };
              });

            const attributes = getAttributeColumns({
              attributes: budgetData.map((bd) => bd.attributes || []),
            });
            const attr = attributeColumnDef(attributes, CompareAttributesLSKey, budgetData);

            this.columnDefs = [
              attr,
              ...this.updateColumnHeaders(fromBudgetVersion, toBudgetVersion, organization),
            ];
            this.autoGroupColumnDef = {
              ...this.autoGroupColumnDef,
              headerComponentParams: {
                ...this.autoGroupColumnDef.headerComponentParams,
                columnsToCollapse: attr.children.map((x: ColDef) => x.colId || x.field),
              },
            };
            this.gridOptions$.next(this.gridOptions);
            this.gridData$.next(budgetData || []);
            this.budgetData.next(budgetData || []);

            this.loading$.next(false);
          }
        )
      );
    })
  );

  doesBudgetDataHaveMissingActivities = false;
  @Input()
  domLayout: 'autoHeight' | 'normal' = 'normal';
  excelOptions = {
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'activity_name_label':
          return 490;
        case 'group0':
          return 280;
        default:
          return 105;
      }
    },
    fileName: 'auxilius-budget-compare.xlsx',
    sheetName: 'Compare Budgets',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.cost_category;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi;
  gridAPI$: ReplaySubject<GridApi> = new ReplaySubject<GridApi>(1);
  org_currency: Currency;
  showOnlyMissingActivities = false;
  userClickedHideUnchanged = false;

  autoSize() {
    this.gridAPI.sizeColumnsToFit();
  }

  constructor() {
    this.budget$.pipe(takeUntilDestroyed()).subscribe();
    this.org_currency = this.getOrgCurrency();
  }

  createExcelStyles(): ExcelStyle[] {
    let orgCurrencies = this.organizationQuery.getAllVendors().map((x) => x.currency as string);

    if (orgCurrencies.length === 0) {
      orgCurrencies = Utils.CURRENCY_OPTIONS;
    }

    return Utils.generateExcelCurrencyStyles(orgCurrencies);
  }

  filter(filterName: string, hide: boolean) {
    if (!this.gridAPI) {
      return;
    }

    const groups = ['cost_category', 'group0', 'group1', 'group2', 'group3', 'group4'];

    if (hide) {
      this.gridAPI.removeRowGroupColumns(groups);
      AgSetColumnsVisible({
        gridApi: this.gridAPI,
        keys: groups,
        visible: false,
      });
    } else {
      this.gridAPI.addRowGroupColumns(groups);
    }

    const filterInstance = this.gridAPI.getFilterInstance(filterName);

    if (filterInstance) {
      filterInstance.setModel(hide ? { values: ['true'] } : null);
    }

    this.gridAPI.onFilterChanged();

    const columnDefs = this.gridAPI.getColumnDefs() || [];

    columnDefs.forEach((c) => {
      if (c.headerName === 'Attributes') {
        (c as ColGroupDef).children.forEach((z) => {
          (z as ColDef).sortable = hide;
        });
      }
    });
    this.columnDefs = columnDefs;
  }

  @Input()
  set fromBudgetVersion(v: CompareBudgetVersion | null) {
    if (v) {
      this.fromBudgetVersion$.next(v);
    }
  }

  getCellClass = () => (params: CellClassParams) => {
    if (params?.node?.data?.contract_direct_cost_currency) {
      return [
        `budgetCost${params?.node?.data?.contract_direct_cost_currency}`,
        'ag-cell-align-right',
      ];
    }

    return ['budgetCostNoSymbol', 'ag-cell-align-right'];
  };

  getDynamicExcelParamsCallback(columns: string[]) {
    return (): ExcelExportParams => {
      const findColumn = (str: string) => columns.findIndex((c) => c === str);

      columns.splice(findColumn('from_unit'), 0, 'from_activity_no');
      columns.splice(findColumn('to_unit') + 1, 0, 'to_activity_no');

      const columnKeys = [
        'cost_category',
        'group0',
        'display_label',
        'activity_name_label',
        ...columns,
      ];

      const attributes = this.gridAPI
        ?.getColumns()
        ?.map((x) => [x.getColId(), x] as [string, Column])
        .filter(([x]: [string, Column]) => {
          return x.startsWith('custom_attr_') || ['account', 'dept', 'po'].includes(x);
        })
        .map(([x, c]) => {
          return [c.isVisible(), x] as [boolean, string];
        });

      const shownAttributes = attributes?.filter(([bool]) => bool).length || 0;

      if (!this.gridAPI) {
        return {};
      }

      const name = this.mainQuery.getSelectedTrial()?.short_name;
      const { from_total_cost, to_total_cost, variance_total_cost } = this.getTotalCost();

      return {
        appendContent: [
          {
            cells: [
              {
                data: { type: 'String', value: `Total` },
                mergeAcross: 3 + shownAttributes,
                styleId: 'total_row_header',
              },
              {
                // initial monthly accrual total
                data: { type: 'Number', value: `${from_total_cost}` },
                mergeAcross: 3,
                styleId: `total_row_${this.org_currency}`,
              },
              {
                // adjustment total
                data: { type: 'Number', value: `${to_total_cost}` },
                mergeAcross: 3,
                styleId: `total_row_${this.org_currency}`,
              },
              {
                // total monthly accrual total
                data: { type: 'Number', value: `${variance_total_cost}` },
                mergeAcross: 2,
                styleId: `total_row_${this.org_currency}`,
              },
              {
                data: {
                  type: 'Number',
                  value: `${(variance_total_cost || 0) / (from_total_cost || 1)}`,
                },
                styleId: 'total_row_percent',
              },
            ],
          },
        ],
        columnKeys,
        prependContent: [
          {
            cells: [
              {
                data: { type: 'String', value: `Trial: ${name}` },
                mergeAcross: 1,
                styleId: 'first_row',
              },
            ],
          },
        ],
      };
    };
  }

  getOrgCurrency() {
    const vendors = this.organizationQuery.getAllVendors();

    if (vendors.length === 1) {
      this.organizationStore.setActive(vendors[0].id);
    }

    return this.organizationQuery.getActive()
      ? this.organizationQuery.getActive()?.currency || Currency.USD
      : Currency.USD;
  }

  getPinnedTotalRow() {
    const { from_total_cost, to_total_cost, variance_total_cost } = this.gridAPI
      .getRenderedNodes()
      .filter((rowNode) => rowNode.level === 0)
      .reduce(
        (acc, val) => {
          const getData = (str: string) => {
            return (val.group ? val.aggData[str] : val.data[str]) || 0;
          };

          acc.from_total_cost += getData('from_total_cost');
          acc.to_total_cost += getData('to_total_cost');
          acc.variance_total_cost += getData('variance_total_cost');

          return acc;
        },
        { from_total_cost: 0, to_total_cost: 0, variance_total_cost: 0 }
      );

    return {
      from_total_cost,
      to_total_cost,
      variance_total_cost,
      variance_total_percent: (variance_total_cost || 0) / (from_total_cost || 1),
    };
  }

  getTotalCost() {
    return this.gridData$.getValue().reduce(
      (acc, val) => {
        acc.from_total_cost += val.from_total_cost || 0;
        acc.to_total_cost += val.to_total_cost || 0;
        acc.variance_total_cost += val.variance_total_cost;

        return acc;
      },
      {
        from_total_cost: 0,
        to_total_cost: 0,
        variance_total_cost: 0,
      }
    );
  }

  hideUnchangedActivities(hide: boolean) {
    this.userClickedHideUnchanged = hide;
    this.filter('changed', hide);
  }

  onDataRendered(e: FirstDataRenderedEvent) {
    this.gridAPI = e.api;
    this.gridAPI$.next(e.api);

    const allColumnIds: string[] = [];

    this.gridAPI.getColumns()?.forEach((column: Column) => {
      allColumnIds.push(column.getColId());
    });
    this.gridAPI.autoSizeColumns(allColumnIds, false);
    const pinnedTotalRow = this.getPinnedTotalRow();

    this.gridAPI?.setGridOption('pinnedBottomRowData', [
      merge(
        {
          activity_name: 'Total',
        },
        pinnedTotalRow
      ),
    ]);
    this.gridAPI.sizeColumnsToFit();
  }

  onFilterChanged() {
    const pinnedTotalRow = this.getPinnedTotalRow();

    this.gridAPI?.setGridOption('pinnedBottomRowData', [
      merge(
        {
          activity_name: 'Total',
        },
        pinnedTotalRow
      ),
    ]);
  }

  setColumnDefs() {
    this.defaultColumns = [
      { field: 'vendor_name', headerName: 'Vendor Contract', hide: true, rowGroup: true },
      {
        children: [
          {
            field: 'cost_category',
            headerClass: 'ag-header-align-left',
            headerName: 'Cost Category',
            hide: true,
            rowGroup: true,
          },
          {
            field: 'group0',
            headerClass: 'ag-header-align-left',
            headerName: 'Category',
            hide: true,
            rowGroup: true,
          },
          {
            field: 'group1',
            headerClass: 'ag-header-align-left',
            headerName: 'Category',
            hide: true,
            rowGroup: true,
          },
          {
            field: 'group2',
            headerClass: 'ag-header-align-left',
            headerName: 'Category',
            hide: true,
            rowGroup: true,
          },
          {
            field: 'group3',
            headerClass: 'ag-header-align-left',
            headerName: 'Category',
            hide: true,
            rowGroup: true,
          },
          {
            field: 'group4',
            headerClass: 'ag-header-align-left',
            headerName: 'Category',
            hide: true,
            rowGroup: true,
          },
        ],
        colId: 'trial-vend-name',
        headerClass: 'ag-header-align-left',
        headerName: 'trial-vend-name',
        hide: false,
      },
      {
        field: 'display_label',
        headerClass: 'ag-header-align-left',
        headerName: 'Label',
        hide: true,
        rowGroup: false,
      },
      {
        field: 'activity_name_label',
        headerClass: 'ag-header-align-left',
        headerName: 'Activities',
        hide: true,
        rowGroup: false,
      },
      {
        children: [
          {
            field: 'from_activity_no',
            headerClass: 'ag-header-align-left',
            headerName: 'Activity ID',
            hide: true,
            rowGroup: false,
          },
          {
            cellClass: ['ag-cell-align-right', 'budget-unit'],
            colId: 'from_unit',
            field: 'from_unit',
            headerClass: 'ag-header-align-center',
            headerName: 'Units',
            minWidth: 60,
            resizable: true,
            valueFormatter: Utils.unitFormatter,
            width: 150,
          },
          {
            cellClass: this.getCellClass(),
            colId: 'from_unit_cost',
            field: 'from_unit_cost',
            headerClass: 'ag-header-align-center',
            headerName: 'Unit Cost',
            minWidth: 120,
            resizable: true,
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value) {
                return Utils.currencyFormatter(Number(params.value), {}, this.org_currency);
              } else {
                return Utils.zeroHyphen;
              }
            },
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: this.getCellClass(),
            colId: 'from_total_cost',
            field: 'from_total_cost',
            headerClass: 'ag-header-align-center',
            headerName: 'Total',
            minWidth: 120,
            resizable: true,
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value) {
                return Utils.currencyFormatter(Number(params.value), {}, this.org_currency);
              } else {
                return Utils.zeroHyphen;
              }
            },
            width: 150,
          },
        ],
        colId: 'Baseline',
        headerClass: 'ag-header-align-center',
        headerName: 'Current Budget (Baseline)',
      },
      TableConstants.SPACER_COLUMN,
      {
        children: [
          {
            field: 'to_activity_no',
            headerClass: 'ag-header-align-left',
            headerName: 'Activity ID',
            hide: true,
            rowGroup: false,
          },
          {
            cellClass: ['ag-cell-align-right', 'budget-unit'],
            colId: 'to_unit',
            field: 'to_unit',
            headerClass: 'ag-header-align-center',
            headerName: 'Units',
            minWidth: 60,
            resizable: true,
            valueFormatter: Utils.unitFormatter,
            width: 150,
          },
          {
            cellClass: this.getCellClass(),
            colId: 'to_unit_cost',
            field: 'to_unit_cost',
            headerClass: 'ag-header-align-center',
            headerName: 'Unit Cost',
            minWidth: 120,
            resizable: true,
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value) {
                return Utils.currencyFormatter(Number(params.value), {}, this.org_currency);
              } else {
                return Utils.zeroHyphen;
              }
            },
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: this.getCellClass(),
            colId: 'to_total_cost',
            field: 'to_total_cost',
            headerClass: 'ag-header-align-center',
            headerName: 'Total',
            minWidth: 120,
            resizable: true,
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value) {
                return Utils.currencyFormatter(Number(params.value), {}, this.org_currency);
              } else {
                return Utils.zeroHyphen;
              }
            },
            width: 150,
          },
        ],
        colId: 'ToBudget',
        headerClass: 'ag-header-align-center',
        headerName: 'To Budget',
      },
      TableConstants.SPACER_COLUMN,
      {
        children: [
          {
            cellClass: ['ag-cell-align-right', 'budget-unit'],
            cellRenderer: AgPulseMinusComponent,
            colId: 'variance_unit',
            editable: false,
            field: 'variance_unit',
            headerClass: 'ag-header-align-center',
            headerName: 'Units',
            minWidth: 80,
            resizable: true,
            valueFormatter: Utils.unitFormatter,
            width: 150,
          },
          {
            cellClass: this.getCellClass(),
            cellRenderer: AgPulseMinusComponent,
            cellRendererParams: {
              org_currency: this.org_currency,
            },
            colId: 'variance_unit_cost',
            field: 'variance_unit_cost',
            headerClass: 'ag-header-align-center',
            headerName: 'Unit Cost',
            minWidth: 120,
            resizable: true,
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: this.getCellClass(),
            cellRenderer: AgPulseMinusComponent,
            cellRendererParams: {
              org_currency: this.org_currency,
            },
            colId: 'variance_total_cost',
            field: 'variance_total_cost',
            headerClass: 'ag-header-align-center',
            headerName: `Total (${Utils.getCurrenySymbol(this.org_currency || Currency.USD)})`,
            minWidth: 120,
            resizable: true,
            width: 150,
          },
          {
            aggFunc: (params) => {
              let var_total = 0;
              let from_total = 0;

              for (const childRow of params.rowNode.allLeafChildren) {
                var_total += childRow.data.variance_total_cost || 0;
                from_total += childRow.data.from_total_cost || 0;
              }

              return from_total ? (var_total || 0) / (from_total || 1) : 0;
            },
            cellClass: ['ag-cell-align-right', 'budget-percent'],
            cellRenderer: AgPulseMinusComponent,
            colId: 'variance_total_percent',
            field: 'variance_total_percent',
            headerClass: 'ag-header-align-center',
            headerName: 'Total (%)',
            minWidth: 80,
            resizable: true,
            width: 150,
          },
        ],
        headerClass: 'ag-header-align-center',
        headerName: 'Variance',
      },
      {
        field: 'changed',
        filter: true,
        headerName: 'Changed',
        hide: true,
      },
      {
        field: 'missing_activity',
        filter: true,
        headerName: 'Missing Activities',
        hide: true,
      },
    ];
  }

  shouldCheckHideUnchanged() {
    return this.showOnlyMissingActivities || this.userClickedHideUnchanged;
  }

  shouldDisableHideUnchanged() {
    return this.showOnlyMissingActivities;
  }

  showMissingActivities(onlyShowMissingActivities: boolean) {
    this.showOnlyMissingActivities = onlyShowMissingActivities;
    this.filter('missing_activity', onlyShowMissingActivities);

    if (!this.showOnlyMissingActivities && this.userClickedHideUnchanged) {
      this.hideUnchangedActivities(true);
    }
  }

  @Input()
  set toBudgetVersion(v: CompareBudgetVersion | null) {
    if (v) {
      this.toBudgetVersion$.next(v);
    }
  }

  updateColumnHeaders(
    from: CompareBudgetVersion,
    to: CompareBudgetVersion,
    org: OrganizationModel
  ) {
    return this.defaultColumns.map((col) => {
      if ('colId' in col) {
        switch (col.colId) {
          case 'Baseline':
            return {
              ...col,
              headerName: from.budget_name,
            };
          case 'ToBudget':
            return {
              ...col,
              headerName: to.budget_name,
            };
          case 'trial-vend-name':
            return {
              ...col,
              headerName: `Vendor Contract: ${org.name}`,
            };
          default:
            return col;
        }
      }

      return col;
    });
  }
}
